import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export type CstIaspConversation = {
  conversationId?: string;
  conversationTitle?: string;
  title?: string;
  _id?: string;
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
};

function getConversationId(conversation: CstIaspConversation): string | undefined {
  return (
    conversation.conversationId ||
    (typeof conversation._id === "string" ? conversation._id : undefined) ||
    (typeof conversation.id === "string" ? conversation.id : undefined)
  );
}

function getConversationTitle(conversation: CstIaspConversation): string {
  return (
    conversation.conversationTitle ||
    conversation.title ||
    (getConversationId(conversation) ? "Untitled" : "Conversation")
  );
}

type Props = {
  conversations: CstIaspConversation[];
  selectedConversationId: string;
  onSelectConversationId: (conversationId: string) => void;
  onDeleteConversationId: (conversationId: string) => void;
  isLoading?: boolean;
};

const ConversationSidebar: React.FC<Props> = ({
  conversations,
  selectedConversationId,
  onSelectConversationId,
  onDeleteConversationId,
  isLoading,
}) => {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Conversations
        </Typography>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 2,
              color: "text.secondary",
            }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {conversations
              .map((conversation) => ({
                id: getConversationId(conversation),
                title: getConversationTitle(conversation),
              }))
              .filter((item) => typeof item.id === "string" && item.id.trim() !== "")
              .map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    selected={item.id === selectedConversationId}
                    onClick={() => onSelectConversationId(item.id!)}
                  >
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        noWrap: true,
                        fontSize: 13,
                      }}
                    />

                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Delete conversation"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteConversationId(item.id!);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}

            {conversations.length === 0 && (
              <Box sx={{ px: 2, py: 2, color: "text.secondary" }}>
                <Typography variant="body2">No conversations</Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ConversationSidebar;
